import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LockIcon from '@mui/icons-material/Lock';

// Import de l'image de fond
import formation1 from '../assets/image1.png';

const RegisterWrapper = styled(Box)(() => ({
  minHeight: '100vh',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 20px',
  backgroundImage: `url(${formation1})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
}));

const Overlay = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
}));

const RegisterCard = styled(Paper)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  width: '100%',
  maxWidth: 500,
  padding: theme.spacing(4),
  borderRadius: 12,
  boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
  background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(245,245,245,0.98))',
  backdropFilter: 'blur(10px)',
}));

const Title = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 900,
  textAlign: 'center',
  marginBottom: theme.spacing(4),
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

const StyledTextField = styled(TextField)(({ theme, error }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
      boxShadow: `0 0 0 2px ${error ? theme.palette.error.main : theme.palette.primary.main}`,
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #c62828 0%, #a61d1d 100%)',
  color: '#fff',
  padding: theme.spacing(1.5),
  borderRadius: 8,
  fontWeight: 700,
  fontSize: '1rem',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #a61d1d 0%, #8b1a1a 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 20px rgba(166, 29, 29, 0.3)',
  },
  '&:disabled': {
    background: '#cccccc',
    transform: 'none',
    boxShadow: 'none',
  },
}));

const LoginLink = styled(Link)(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'none',
  fontWeight: 600,
  transition: 'all 0.3s ease',
  '&:hover': {
    color: '#764ba2',
    textDecoration: 'underline',
  },
}));

const UserRegister = ({ onRegister }) => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    password: '',
    confirmPassword: '',
    telephone: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [serverError, setServerError] = useState('');
  
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nom.trim()) newErrors.nom = 'Le nom est requis';
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    if (!formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    if (formData.telephone && !/^[0-9+\-\s()]{10,15}$/.test(formData.telephone)) {
      newErrors.telephone = 'Numéro de téléphone invalide';
    }

    setErrors(newErrors);
    setServerError('');
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Effacer l'erreur quand l'utilisateur commence à taper
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    if (serverError) setServerError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const userData = {
        nom: formData.nom,
        email: formData.email,
        password: formData.password,
        telephone: formData.telephone || null,
        type: 'user'
      };

      const res = await axios.post(`${API_URL}/users`, userData);
      
      if (res.data?.success) {
        alert('Inscription réussie ! Vous pouvez maintenant vous connecter.');
        if (onRegister) onRegister(res.data.data);
        navigate('/login');
      } else {
        setServerError(res.data?.message || 'Échec de l\'inscription');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          err.message || 
                          'Erreur lors de l\'inscription';
      setServerError(errorMessage);
      
      if (err.response?.status === 400 && err.response?.data?.message.includes('email')) {
        setErrors(prev => ({
          ...prev,
          email: 'Cet email est déjà utilisé'
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <RegisterWrapper>
      <Overlay />
      
      <Container maxWidth="lg">
        <Grid container justifyContent="center">
          <Grid item xs={12} md={8} lg={6}>
            <RegisterCard elevation={6}>
              <Title variant="h3" gutterBottom>
                Créer un compte
              </Title>
              
              {serverError && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {serverError}
                </Alert>
              )}
              
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* Nom */}
                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      label="Nom complet *"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      error={!!errors.nom}
                      helperText={errors.nom}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      placeholder="Votre nom"
                      required
                    />
                  </Grid>

                  {/* Email */}
                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      label="Email *"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      error={!!errors.email}
                      helperText={errors.email}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      placeholder="votre@email.com"
                      required
                    />
                  </Grid>

                  {/* Téléphone */}
                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      label="Téléphone (optionnel)"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      error={!!errors.telephone}
                      helperText={errors.telephone}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      placeholder="0123456789"
                    />
                  </Grid>

                  {/* Mot de passe */}
                  <Grid item xs={12} md={6}>
                    <StyledTextField
                      fullWidth
                      label="Mot de passe *"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      error={!!errors.password}
                      helperText={errors.password}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleClickShowPassword}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      placeholder="Au moins 6 caractères"
                      required
                    />
                  </Grid>

                  {/* Confirmation mot de passe */}
                  <Grid item xs={12} md={6}>
                    <StyledTextField
                      fullWidth
                      label="Confirmer le mot de passe *"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleClickShowConfirmPassword}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      placeholder="Retapez votre mot de passe"
                      required
                    />
                  </Grid>

                  {/* Bouton d'inscription */}
                  <Grid item xs={12}>
                    <SubmitButton
                      fullWidth
                      type="submit"
                      disabled={loading}
                      size="large"
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "S'inscrire"
                      )}
                    </SubmitButton>
                  </Grid>

                  {/* Lien vers la connexion */}
                  <Grid item xs={12}>
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Typography variant="body1" color="text.secondary">
                        Déjà un compte ?{' '}
                        <LoginLink to="/login">
                          Se connecter
                        </LoginLink>
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </form>

              {/* Conditions */}
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ 
                    display: 'block',
                    lineHeight: 1.5,
                    opacity: 0.8
                  }}
                >
                  En vous inscrivant, vous acceptez nos conditions d'utilisation 
                  et notre politique de confidentialité.
                </Typography>
              </Box>
            </RegisterCard>
          </Grid>
        </Grid>
      </Container>
    </RegisterWrapper>
  );
};

export default UserRegister;